/**
 * JobsWidget — small navbar dropdown showing the current user's
 * background jobs (queued, running, succeeded, failed).
 *
 * 2026-08-04 — the widget no longer runs its own `/api/jobs` poller.
 * It now reads exclusively from `JobsContext`, which polls once app-
 * wide on an adaptive 10s / 30s cadence. This eliminates a whole
 * duplicate stream of QPS on the loudest route in prod (was ~3170
 * req/hr on the dashboard before). The transition-detection and
 * first-load attention-surfacing logic that used to live inside the
 * widget's `fetchJobs` now runs in a `useEffect` that watches the
 * context's `rows`, so the same toast semantics are preserved
 * (job "done" / "failed" / "awaiting approval" popups still fire).
 *
 * Behavioural trade-off: the popover's progress bar for a running
 * job now refreshes on the context's 10s cadence instead of the
 * widget's old 3s cadence when the popover was open. Acceptable per
 * product — the per-block CourseBuilder fast-poller still gives
 * users sub-second progress at the actual work surface.
 *
 * Renders nothing when the user has zero jobs in the last 6h — keeps
 * the navbar clean for users who never touch AI generation.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, Clock, Briefcase, ExternalLink, X, ChevronRight } from 'lucide-react';
import api from '../api/client';
import { useJobs } from '../context/JobsContext';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// Map status → (icon, color, label) for a consistent visual language.
const STATUS_META = {
  queued:    { Icon: Clock,        color: 'text-amber-500',  label: 'Queued' },
  running:   { Icon: Loader2,      color: 'text-blue-500',   label: 'Running', spin: true },
  succeeded: { Icon: CheckCircle2, color: 'text-emerald-600', label: 'Done' },
  failed:    { Icon: XCircle,      color: 'text-red-500',    label: 'Failed' },
  cancelled: { Icon: XCircle,      color: 'text-gray-400',   label: 'Cancelled' },
};


function timeAgo(iso) {
  if (!iso) return '';
  // Backend stores datetimes as naive UTC in Mongo (BSON drops tzinfo).
  // FastAPI then serializes them as `"2026-02-24T22:27:48.937"` with
  // NO `Z` suffix — JavaScript parses that as *local time* and we end
  // up displaying every fresh job as "5–6h ago" for IST users.
  // Force-append `Z` when the string has no timezone marker so it's
  // unambiguously parsed as UTC.
  let s = String(iso);
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const ms = Date.now() - new Date(s).getTime();
  if (ms < 0) return 'just now'; // clock skew → never display "in the future"
  if (ms < 60_000) return 'just now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}


// Fire a one-shot toast when a job moves to a terminal state OR to the
// `stage1_complete` awaiting-approval halt (video pipeline). Reads the
// job's deep link (set by the worker for PPT / video / lab generations)
// and exposes it as the toast action so the user can jump straight to
// the relevant block without opening the widget popover.
//
// For video jobs we also detect the intermediate `running` → "Stage 1
// complete — awaiting approval" transition via the `progressMessage`
// field. The job's `status` stays `running` (unified shape) but the
// pipeline is paused waiting for the creator to approve the script. A
// proactive toast removes the "where do I click next?" confusion.
function _notifyTransition(job, prevJob, navigate) {
  const deepLink = job?.result?.deepLink;
  const label = job.label || job.kind || 'Background job';
  const goToBlock = deepLink
    ? { label: 'View', onClick: () => navigate(deepLink) }
    : undefined;
  if (job.status === 'succeeded' && prevJob?.status !== 'succeeded') {
    toast.success(`${label} — done`, {
      description: deepLink ? 'Click View to jump to the block.' : 'Refresh the page to see updated content.',
      duration: 8000,
      action: goToBlock,
    });
  } else if (job.status === 'failed' && prevJob?.status !== 'failed') {
    toast.error(`${label} — failed`, {
      description: (job.error || 'Unknown error').slice(0, 200),
      duration: 12000,
      // Failure toasts ALWAYS need a "View" action so the user can
      // jump to the block and click Retry without hunting through the
      // course tree. Without this the error toast was a dead-end —
      // user reported "no popup to fix it". Mar 02 2026.
      action: goToBlock,
    });
  } else if (
    job.status === 'running' &&
    (job.progressMessage || '').toLowerCase().includes('awaiting approval') &&
    !(prevJob?.progressMessage || '').toLowerCase().includes('awaiting approval')
  ) {
    // Stage 1 of the video pipeline is done; the orchestrator halts
    // here until the creator approves the script (Approve & continue).
    // A toast with a deep-link to the block is the single clearest way
    // to surface this — otherwise the job just sits at 33% in the pill
    // forever from the creator's perspective. Mar 02 2026 — user
    // report.
    toast.message(`${label} — script ready for review`, {
      description: 'Stage 1 complete. Approve the script to start Stage 2 (asset gathering).',
      duration: 15000,
      action: goToBlock && { ...goToBlock, label: 'Review script' },
    });
  }
}

// sessionStorage key prefix for "we already toasted this job in this
// session" — used by the first-load scan below so a user refreshing
// the page mid-batch doesn't get spammed with stale notifications,
// but a logout/login cycle (which clears sessionStorage) gives them
// a fresh re-notification of unresolved attention items.
const SESS_KEY = 'elv.jobsWidget.toasted';
function _alreadyToasted(jobId, eventKey) {
  try {
    const raw = sessionStorage.getItem(SESS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return map[`${jobId}:${eventKey}`] === true;
  } catch (_e) { return false; }
}
function _markToasted(jobId, eventKey) {
  try {
    const raw = sessionStorage.getItem(SESS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[`${jobId}:${eventKey}`] = true;
    // Cap the map size — sessionStorage caps at ~5 MB but we have no
    // business storing more than a few hundred entries. Drop oldest.
    const keys = Object.keys(map);
    if (keys.length > 200) {
      keys.slice(0, keys.length - 200).forEach((k) => delete map[k]);
    }
    sessionStorage.setItem(SESS_KEY, JSON.stringify(map));
  } catch (_e) { /* ignore quota / private-mode failures */ }
}

// One-shot first-load surfacing for jobs that NEED user attention but
// transitioned to that state BEFORE this widget mounted. The transition
// detector above only fires when the page is open as the state flip
// happens — if the user navigates in 2 minutes after a job hit
// "awaiting approval" or "failed", they'd never see the toast.
// Mar 02 2026 — user confirmed bug: "i still didn't get any popups for
// awaiting approval" despite the screenshot showing jobs in that state.
//
// Dedup is sessionStorage-keyed by `jobId:event` so a refresh doesn't
// re-fire, but a fresh session (new tab, new login) re-surfaces.
function _surfaceAttentionItemsOnFirstLoad(jobs, navigate) {
  for (const job of jobs) {
    const deepLink = job?.result?.deepLink;
    const label = job.label || job.kind || 'Background job';
    const goToBlock = deepLink
      ? { label: 'View', onClick: () => navigate(deepLink) }
      : undefined;
    if (job.status === 'failed' && !_alreadyToasted(job.id, 'failed')) {
      toast.error(`${label} — failed`, {
        description: (job.error || 'Unknown error').slice(0, 200),
        duration: 12000,
        action: goToBlock,
      });
      _markToasted(job.id, 'failed');
    } else if (
      job.status === 'running' &&
      (job.progressMessage || '').toLowerCase().includes('awaiting approval') &&
      !_alreadyToasted(job.id, 'awaiting_approval')
    ) {
      toast.message(`${label} — script ready for review`, {
        description: 'Stage 1 complete. Approve the script to start Stage 2 (asset gathering).',
        duration: 15000,
        action: goToBlock && { ...goToBlock, label: 'Review script' },
      });
      _markToasted(job.id, 'awaiting_approval');
    }
  }
}


export default function JobsWidget() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // Single source of truth for job rows — the app-wide JobsContext
  // poller. Was previously a local `/api/jobs` poller with its own
  // 3s/30s adaptive cadence; retired 2026-08-04 to cut prod QPS.
  const { rows, refresh } = useJobs();
  // Cap the dropdown at the 10 most-recent rows — the navbar widget
  // is the "what happened recently?" affordance, not "every job ever".
  // The bottom-right `GlobalJobsPill` still surfaces the full active
  // set. Feb 28 2026 — user report.
  const jobs = useMemo(() => rows.slice(0, 10), [rows]);
  // Track previous full job snapshot per id so we can detect more
  // nuanced transitions than just the terminal `status` enum — e.g.
  // the video pipeline's intermediate "awaiting approval" halt which
  // surfaces via `progressMessage`, not `status`. `null` on the very
  // first render so we don't toast historical jobs the user already
  // knows about.
  const prevJobsRef = useRef(null);

  // Transition detection — runs whenever the context pushes a new
  // `rows` snapshot into the widget. This is a straight port of the
  // logic that used to live inside the widget's own `fetchJobs`;
  // moving it here preserves the toast semantics (done / failed /
  // awaiting-approval) without owning the fetch.
  useEffect(() => {
    if (prevJobsRef.current !== null) {
      const prev = prevJobsRef.current;
      for (const job of jobs) {
        const before = prev.get(job.id);
        if (!before) continue;
        _notifyTransition(job, before, navigate);
      }
    } else if (jobs.length > 0) {
      // First-load surfacing: the transition detector can't catch
      // jobs that entered "failed" or "awaiting approval" BEFORE the
      // widget mounted. Scan the initial snapshot and toast once per
      // attention-needing job (deduped via sessionStorage so a
      // refresh doesn't re-fire).
      _surfaceAttentionItemsOnFirstLoad(jobs, navigate);
    }
    // Update the ref AFTER toast decision so we capture the snapshot
    // used by the next comparison. Store the whole row so transition
    // detection has access to `progressMessage`, `result.deepLink`,
    // etc. Guarded so we don't set to a Map before the first non-
    // empty snapshot arrives — otherwise the first-load surfacing
    // would never run for a user who logged in AFTER a job started.
    if (jobs.length > 0 || prevJobsRef.current !== null) {
      prevJobsRef.current = new Map(jobs.map((j) => [j.id, j]));
    }
  }, [jobs, navigate]);

  // Derive the badge count from the same rows the popover renders,
  // so the count and list are always consistent. Anything queued or
  // running counts.
  const activeCount = jobs.filter(
    (j) => j.status === 'queued' || j.status === 'running',
  ).length;

  const triggerSmoke = async () => {
    try {
      await api.post('/jobs/smoke');
      // Refetch immediately so the new job appears without waiting
      // for the next context poll tick.
      refresh();
    } catch (e) {
      // No toast — the empty state will stay; user can retry.
    }
  };

  // Cancel a queued/running job — works for both arq-backed and legacy
  // block_gen_jobs (backend cancel endpoint covers both stores). The
  // stopPropagation prevents the row's deep-link click from firing.
  const cancelJob = async (e, jobId) => {
    e.stopPropagation();
    try {
      await api.post(`/jobs/${jobId}/cancel`);
      toast.success('Job cancelled');
      refresh();
    } catch (err) {
      toast.error(err?.message || 'Could not cancel job');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2 hover:bg-orange-50"
          data-testid="jobs-widget-trigger"
          aria-label="Background jobs"
        >
          <Briefcase className="w-5 h-5 text-gray-600 hover:text-orange-600" />
          {activeCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center transition-transform animate-in zoom-in-50"
              data-testid="jobs-widget-active-count"
            >
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="jobs-widget-popover">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm">Background Jobs</div>
          <Badge variant="secondary" className="text-[10px]">{jobs.length}</Badge>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {jobs.length === 0 ? (
            <div className="p-6 text-center" data-testid="jobs-empty-state">
              <Briefcase className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <div className="text-sm text-gray-600 mb-1">No background jobs yet</div>
              <div className="text-[11px] text-gray-400 mb-3">
                Long-running tasks (AI presentation generation, video pipeline runs) will show up here.
              </div>
              <Button
                size="sm" variant="outline" onClick={triggerSmoke}
                data-testid="jobs-run-smoke"
              >
                Run a smoke-test job (~8s)
              </Button>
            </div>
          ) : (
            jobs.map((job) => {
              const meta = STATUS_META[job.status] || STATUS_META.queued;
              const Icon = meta.Icon;
              const deepLink = job?.result?.deepLink;
              // Allow click-through for any status that has a deepLink
              // (running / queued / succeeded / failed). Creators
              // explicitly asked for running video rows to be
              // navigable so they can jump to the block page and watch
              // Stage 2/3 progress in context. The widget will still
              // collapse on navigation thanks to the `setOpen(false)`
              // wrapper. Feb 28 2026 — user report.
              const clickable = !!deepLink;
              const onRowClick = clickable
                ? () => { setOpen(false); navigate(deepLink); }
                : undefined;
              return (
                <div
                  key={job.id}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={onRowClick}
                  onKeyDown={clickable
                    ? (e) => { if (e.key === 'Enter') onRowClick(); }
                    : undefined}
                  className={`px-4 py-3 border-b last:border-b-0 transition-colors animate-in fade-in slide-in-from-top-1 duration-200 ${clickable ? 'cursor-pointer hover:bg-orange-50' : 'hover:bg-gray-50'}`}
                  data-testid={`job-row-${job.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color} ${meta.spin ? 'animate-spin' : ''}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        {/* Label + deep-link arrow. The arrow is a SIBLING
                            of the truncated label (not nested inside the
                            same truncate) so a long label clips itself
                            but doesn't push the arrow out of view. The
                            user reported the arrow was invisible on
                            video rows because the truncate was eating
                            it. Mar 02 2026. */}
                        <div className="min-w-0 flex-1 flex items-center gap-1.5">
                          <div className="text-sm font-medium text-gray-800 truncate min-w-0">
                            {job.label || job.kind}
                          </div>
                          {clickable && (
                            <ExternalLink
                              className="w-3.5 h-3.5 text-orange-500 flex-shrink-0"
                              data-testid={`job-deeplink-${job.id}`}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={`text-[10px] font-mono uppercase ${meta.color}`}>
                            {meta.label}
                          </span>
                          {/* Cancel button — only for queued/running jobs.
                              Backend marks the row failed; if the underlying
                              task is mid-LLM it'll still finish, but the UI
                              stops showing the spinner immediately. */}
                          {(job.status === 'running' || job.status === 'queued') && (
                            <button
                              type="button"
                              onClick={(e) => cancelJob(e, job.id)}
                              className="p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                              title="Cancel job"
                              data-testid={`job-cancel-${job.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Breadcrumb — Course → Topic → Block. Surfaces
                          the full provenance of each background job so
                          creators running parallel batches can tell at
                          a glance WHICH quiz in WHICH topic in WHICH
                          course is being worked on. Backend's
                          `_enrich_breadcrumbs()` populates these from
                          MongoDB in a single batch query per response.
                          Falls back to a partial path or nothing when
                          the entity has been deleted since the job
                          fired. */}
                      {(job.params?.courseTitle || job.params?.topicName || job.params?.blockTitle) && (
                        <div
                          className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-0.5 truncate"
                          data-testid={`job-breadcrumb-${job.id}`}
                          title={[
                            job.params?.courseTitle,
                            job.params?.topicName,
                            job.params?.blockTitle,
                          ].filter(Boolean).join(' › ')}
                        >
                          {job.params?.courseTitle && (
                            <span className="truncate max-w-[110px]">{job.params.courseTitle}</span>
                          )}
                          {job.params?.courseTitle && job.params?.topicName && (
                            <ChevronRight className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />
                          )}
                          {job.params?.topicName && (
                            <span className="truncate max-w-[110px]">{job.params.topicName}</span>
                          )}
                          {job.params?.topicName && job.params?.blockTitle && (
                            <ChevronRight className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />
                          )}
                          {job.params?.blockTitle && (
                            <span className="truncate max-w-[140px] text-gray-700 font-medium">{job.params.blockTitle}</span>
                          )}
                        </div>
                      )}
                      {/* Progress bar for running jobs — CSS transition
                          handles the smooth fill animation as `progress`
                          increments on each context poll tick. */}
                      {job.status === 'running' && (
                        <div className="mt-1.5">
                          <div className="h-1 bg-gray-100 rounded overflow-hidden">
                            <div
                              className="h-full bg-blue-500 transition-all duration-300 ease-out"
                              style={{ width: `${job.progress || 0}%` }}
                            />
                          </div>
                          {job.progressMessage && (
                            <div className="text-[10px] text-gray-500 mt-1">
                              {job.progressMessage}{job.progress ? ` · ${job.progress}%` : ''}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Error detail for failed jobs */}
                      {job.status === 'failed' && job.error && (
                        <div className="text-[11px] text-red-600 mt-1 line-clamp-2">
                          {job.error}
                        </div>
                      )}
                      <div className="text-[10px] text-gray-400 mt-1">
                        {timeAgo(job.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
